(() => {
  "use strict";

  const MOBILE_BREAKPOINT = 768;
  const LIGHTBOX_ID = "native-product-media-lightbox";
  const SWIPE_THRESHOLD = 48;

  const isReducedMotion = () =>
    window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false;

  const circularIndex = (index, length) => {
    if (length <= 0) return 0;
    return ((index % length) + length) % length;
  };

  const parseJSON = (value) => {
    if (!value) return null;

    try {
      return JSON.parse(value);
    } catch (_error) {
      return null;
    }
  };

  const getSelectedVariantId = (element) => {
    const variant = parseJSON(element?.dataset?.selectedVariant);
    return variant?.id == null ? null : String(variant.id);
  };

  const getMediaWrapper = (element) =>
    element?.closest?.("[data-product-media-wrapper]") || null;

  const getVariantMediaMap = (element) => {
    const mediaMap = new Map();
    const mappingElement = getMediaWrapper(element)?.querySelector(
      "[data-native-product-media-variant-map]",
    );
    const mappings = parseJSON(mappingElement?.textContent) || [];

    mappings.forEach(({ mediaId, variantId }) => {
      if (mediaId == null || variantId == null) return;

      const key = String(mediaId);
      mediaMap.set(key, [...(mediaMap.get(key) || []), String(variantId)]);
    });

    return mediaMap;
  };

  const addVariantIdsFromMap = (element, mediaMap) => {
    if (!element || element.dataset.variantIds || element.dataset.variantId)
      return;

    const variantIds = mediaMap.get(String(element.dataset.mediaId));
    if (variantIds?.length) element.dataset.variantIds = variantIds.join(",");
  };

  const normaliseMediaUrl = (url) => {
    if (!url) return "";

    try {
      const parsedUrl = new URL(url, document.baseURI);
      return `${parsedUrl.origin}${parsedUrl.pathname}`;
    } catch (_error) {
      return url.split("?")[0];
    }
  };

  const getVariantIds = (element) => {
    if (!element) return [];

    const directValue = element.dataset.variantIds || element.dataset.variantId;
    if (directValue) {
      return directValue
        .split(/[\s,|]+/)
        .map((id) => id.trim())
        .filter(Boolean);
    }

    const variant = parseJSON(element.dataset.variant);
    return variant?.id == null ? [] : [String(variant.id)];
  };

  const getMediaContainer = (element) =>
    element.closest(
      "[data-media-id], [data-variant-id], [data-variant-ids], [data-variant]",
    ) || element;

  const isRelevantMedia = (element, selectedVariantId) => {
    const variantIds = getVariantIds(getMediaContainer(element));
    return (
      !selectedVariantId ||
      variantIds.length === 0 ||
      variantIds.includes(selectedVariantId)
    );
  };

  const getDeferredVideo = (element) =>
    element.querySelector("video") ||
    element.querySelector("template")?.content?.querySelector("video") ||
    null;

  const getMediaType = (element) => {
    const container = element.closest("[data-media-id], [data-media-type]");
    const declaredType = container?.dataset.mediaType;

    if (declaredType) return declaredType;
    if (getDeferredVideo(element)) return "video";
    return "image";
  };

  const isLightboxMedia = (element) => {
    const type = getMediaType(element);
    if (type === "image") return Boolean(element.querySelector("img"));
    if (type === "video") return Boolean(getDeferredVideo(element));
    return false;
  };

  const toButton = (element) => {
    if (!element) return null;
    if (element.tagName === "BUTTON") return element;

    const button = document.createElement("button");
    [...element.attributes].forEach((attribute) => {
      if (attribute.name !== "href")
        button.setAttribute(attribute.name, attribute.value);
    });
    button.type = "button";
    button.innerHTML = element.innerHTML;
    element.replaceWith(button);
    return button;
  };

  const getLightboxTrigger = (event, root) => {
    const imageLink = event.target.closest("a[data-main-media-link]");
    if (imageLink && root.contains(imageLink) && isLightboxMedia(imageLink)) {
      return imageLink;
    }

    const videoPoster = event.target.closest(
      "[data-native-media-dialog-video]",
    );
    if (
      videoPoster &&
      root.contains(videoPoster) &&
      isLightboxMedia(videoPoster)
    ) {
      return videoPoster;
    }

    return null;
  };

  class ProductMediaLightbox {
    constructor() {
      this.dialog = null;
      this.stage = null;
      this.counter = null;
      this.previousButton = null;
      this.nextButton = null;
      this.closeButton = null;
      this.items = [];
      this.index = 0;
      this.lastTrigger = null;
      this.activeRoot = null;
      this.pointerStart = null;
    }

    ensureDialog() {
      if (this.dialog) return;

      const dialog = document.createElement("dialog");
      dialog.id = LIGHTBOX_ID;
      dialog.className = "native-product-media-lightbox";
      dialog.setAttribute("aria-labelledby", `${LIGHTBOX_ID}-title`);
      dialog.innerHTML = `
        <div class="native-product-media-lightbox__shell">
          <header class="native-product-media-lightbox__header">
            <p id="${LIGHTBOX_ID}-title" class="native-product-media-visually-hidden">Product media viewer</p>
            <p class="native-product-media-lightbox__counter" data-native-media-counter aria-live="polite"></p>
            <button class="native-product-media-lightbox__button native-product-media-lightbox__button--close" type="button" data-native-media-close aria-label="Close media viewer">
              <svg aria-hidden="true" viewBox="0 0 20 20" focusable="false"><path d="M5 5 15 15M15 5 5 15"/></svg>
            </button>
          </header>
          <div class="native-product-media-lightbox__body">
            <button class="native-product-media-lightbox__button native-product-media-lightbox__button--previous" type="button" data-native-media-previous aria-label="Previous media">
              <svg aria-hidden="true" viewBox="0 0 20 20" focusable="false"><path d="m12.5 4-6 6 6 6"/></svg>
            </button>
            <div class="native-product-media-lightbox__stage" data-native-media-stage></div>
            <button class="native-product-media-lightbox__button native-product-media-lightbox__button--next" type="button" data-native-media-next aria-label="Next media">
              <svg aria-hidden="true" viewBox="0 0 20 20" focusable="false"><path d="m7.5 4 6 6-6 6"/></svg>
            </button>
          </div>
        </div>
      `;

      this.dialog = dialog;
      this.stage = dialog.querySelector("[data-native-media-stage]");
      this.counter = dialog.querySelector("[data-native-media-counter]");
      this.previousButton = dialog.querySelector(
        "[data-native-media-previous]",
      );
      this.nextButton = dialog.querySelector("[data-native-media-next]");
      this.closeButton = dialog.querySelector("[data-native-media-close]");

      this.closeButton.addEventListener("click", () => this.close());
      this.previousButton.addEventListener("click", () =>
        this.show(this.index - 1),
      );
      this.nextButton.addEventListener("click", () =>
        this.show(this.index + 1),
      );

      dialog.addEventListener("click", (event) => {
        if (event.target === dialog) this.close();
      });

      dialog.addEventListener("close", () => {
        this.pauseCurrentVideo();
        const trigger = this.lastTrigger;
        this.lastTrigger = null;
        this.activeRoot = null;
        if (trigger?.isConnected) trigger.focus({ preventScroll: true });
      });

      dialog.addEventListener("keydown", (event) => {
        if (event.key === "ArrowLeft") {
          event.preventDefault();
          this.show(this.index - 1);
        }

        if (event.key === "ArrowRight") {
          event.preventDefault();
          this.show(this.index + 1);
        }
      });

      this.stage.addEventListener("pointerdown", (event) => {
        if (event.pointerType === "mouse") return;
        this.pointerStart = {
          id: event.pointerId,
          x: event.clientX,
          y: event.clientY,
        };
      });

      this.stage.addEventListener("pointerup", (event) => {
        if (!this.pointerStart || this.pointerStart.id !== event.pointerId)
          return;

        const horizontalDistance = event.clientX - this.pointerStart.x;
        const verticalDistance = event.clientY - this.pointerStart.y;
        this.pointerStart = null;

        if (
          Math.abs(horizontalDistance) < SWIPE_THRESHOLD ||
          Math.abs(horizontalDistance) < Math.abs(verticalDistance)
        ) {
          return;
        }

        if (horizontalDistance < 0) this.show(this.index + 1);
        if (horizontalDistance > 0) this.show(this.index - 1);
      });

      this.stage.addEventListener("pointercancel", () => {
        this.pointerStart = null;
      });

      document.body.append(dialog);
    }

    getItems(root) {
      return [
        ...root.querySelectorAll(
          "a[data-main-media-link], [data-native-media-dialog-video]",
        ),
      ]
        .filter((trigger) => !trigger.closest("[hidden]"))
        .filter(isLightboxMedia)
        .map((trigger) => {
          const type = getMediaType(trigger);
          const image = trigger.querySelector("img");
          const video = getDeferredVideo(trigger);

          return {
            trigger,
            type,
            alt: image?.alt || "",
            imageSource: trigger.href || image?.currentSrc || image?.src || "",
            video,
          };
        });
    }

    open(root, trigger) {
      const items = this.getItems(root);
      const index = items.findIndex((item) => item.trigger === trigger);

      if (index < 0 || items.length === 0) return false;

      this.ensureDialog();
      this.items = items;
      this.lastTrigger = trigger;
      this.activeRoot = root;
      this.show(index);

      if (typeof this.dialog.showModal === "function") {
        if (!this.dialog.open) this.dialog.showModal();
      } else {
        this.dialog.setAttribute("open", "");
        this.dialog.setAttribute("role", "dialog");
        this.dialog.setAttribute("aria-modal", "true");
      }

      this.closeButton.focus({ preventScroll: true });
      return true;
    }

    close() {
      if (!this.dialog) return;

      if (typeof this.dialog.close === "function" && this.dialog.open) {
        this.dialog.close();
      } else {
        this.dialog.removeAttribute("open");
        this.pauseCurrentVideo();
        const trigger = this.lastTrigger;
        this.lastTrigger = null;
        this.activeRoot = null;
        if (trigger?.isConnected) trigger.focus({ preventScroll: true });
      }
    }

    closeForRoot(root) {
      if (this.activeRoot === root) this.close();
    }

    show(nextIndex) {
      if (!this.items.length) return;

      this.index = circularIndex(nextIndex, this.items.length);
      const item = this.items[this.index];

      this.pauseCurrentVideo();
      this.stage.replaceChildren(this.createMediaElement(item));
      this.counter.textContent = `${this.index + 1} / ${this.items.length}`;
      this.previousButton.disabled = this.items.length < 2;
      this.nextButton.disabled = this.items.length < 2;
      this.previousButton.hidden = this.items.length < 2;
      this.nextButton.hidden = this.items.length < 2;
    }

    createMediaElement(item) {
      if (item.type === "video" && item.video) {
        const video = item.video.cloneNode(true);
        video.removeAttribute("id");
        video.removeAttribute("autoplay");
        video.autoplay = false;
        video.controls = true;
        video.playsInline = true;
        video.preload = "metadata";
        video.muted = false;
        video.defaultMuted = false;

        video.querySelectorAll("source[data-src]").forEach((source) => {
          if (!source.src) source.src = source.dataset.src;
        });

        return video;
      }

      const image = document.createElement("img");
      image.src = item.imageSource;
      image.alt = item.alt;
      image.decoding = "async";
      return image;
    }

    pauseCurrentVideo() {
      this.stage?.querySelector("video")?.pause();
    }
  }

  const lightbox = new ProductMediaLightbox();

  class NativeProductMedia extends HTMLElement {
    connectedCallback() {
      if (this.initialized || this.initializationQueued) return;

      const mainMedia = this.querySelector("[data-slider-main]");
      const track = mainMedia?.querySelector(
        ".product-media-native__track, .swiper-wrapper",
      );

      if (!mainMedia || !track) {
        this.queueInitialization();
        return;
      }

      this.initialized = true;
      this.abortController = new AbortController();
      this.mainMedia = mainMedia;
      this.track = track;
      this.sectionId = this.dataset.sectionId;
      this.selectedVariantId = getSelectedVariantId(this);
      this.variantMediaMap = getVariantMediaMap(this);
      this.frameId = null;
      this.scrollEndTimer = null;
      this.pendingIndex = null;
      this.pointerStart = null;
      this.visibleSlides = [];

      this.setNativeAttributes();
      this.collectSlides();
      this.applyVariantFilter();
      this.attachEvents();
      this.updateActiveSlide();
    }

    queueInitialization() {
      this.initializationQueued = true;
      this.initializationFrame = window.requestAnimationFrame(() => {
        this.initializationQueued = false;
        this.initializationFrame = null;
        if (this.isConnected) this.connectedCallback();
      });
    }

    disconnectedCallback() {
      if (this.initializationFrame)
        window.cancelAnimationFrame(this.initializationFrame);
      this.initializationFrame = null;
      this.initializationQueued = false;
      lightbox.closeForRoot(this.mainMedia);
      this.abortController?.abort();
      this.resizeObserver?.disconnect();
      if (this.frameId) window.cancelAnimationFrame(this.frameId);
      if (this.scrollEndTimer) window.clearTimeout(this.scrollEndTimer);
      if (this.variantHandler)
        window.eventBus?.off?.("variant:change", this.variantHandler);
      this.initialized = false;
    }

    setNativeAttributes() {
      this.dataset.nativeProductMedia = "true";
      this.mainMedia.dataset.nativeMediaScroller = "true";
      this.mainMedia.setAttribute("role", "region");
      this.mainMedia.setAttribute("aria-label", "Product media");
      this.mainMedia.setAttribute("tabindex", "0");
      this.mainMedia.classList.remove(
        "swiper",
        "swiper-initialized",
        "swiper-horizontal",
        "swiper-autoheight",
      );
      this.mainMedia.classList.add("product-media-native__scroller");
      this.track.classList.remove("swiper-wrapper");
      this.track.classList.add("product-media-native__track");
      this.track.style.transform = "none";

      this.navigation = this.mainMedia.querySelector(
        ".media-slider-navigation",
      );
      if (this.navigation?.parentElement === this.mainMedia) {
        this.append(this.navigation);
      }

      this.previousControl = toButton(
        this.navigation?.querySelector('[class*="arrow-prev-"]'),
      );
      this.nextControl = toButton(
        this.navigation?.querySelector('[class*="arrow-next-"]'),
      );
      this.pagination = this.navigation?.querySelector(
        '.product-media-native__pagination, [class*="swiper-pagination"]',
      );

      if (this.pagination) {
        this.pagination.classList.add("product-media-native__pagination");
        this.pagination.setAttribute("aria-live", "polite");
        this.pagination.setAttribute("aria-atomic", "true");
      }

      if (this.previousControl) {
        this.previousControl.setAttribute(
          "aria-label",
          "Previous product media",
        );
        this.previousControl.setAttribute(
          "aria-controls",
          this.mainMedia.id || this.assignScrollerId(),
        );
      }

      if (this.nextControl) {
        this.nextControl.setAttribute("aria-label", "Next product media");
        this.nextControl.setAttribute(
          "aria-controls",
          this.mainMedia.id || this.assignScrollerId(),
        );
      }
    }

    assignScrollerId() {
      const id = `${this.id || "product-media"}-native-scroller`;
      this.mainMedia.id = id;
      return id;
    }

    collectSlides() {
      this.slides = [...this.track.children].filter(
        (element) =>
          element.classList.contains("product-media-native__slide") ||
          element.classList.contains("swiper-slide"),
      );

      this.slides.forEach((slide) => {
        addVariantIdsFromMap(slide, this.variantMediaMap);
        slide.classList.remove(
          "swiper-slide",
          "swiper-slide-active",
          "swiper-slide-next",
          "fadeIn",
          "animated",
        );
        slide.classList.add("product-media-native__slide");
        slide.style.visibility = "visible";
      });

      this.slides
        .flatMap((slide) => [
          ...slide.querySelectorAll("a[data-main-media-link]"),
        ])
        .forEach((anchor, index) => {
          if (!anchor.getAttribute("aria-label")) {
            anchor.setAttribute(
              "aria-label",
              `View product media ${index + 1}`,
            );
          }
        });
    }

    hydrateSlide(slide) {
      if (!slide) return;

      slide.querySelectorAll("img[data-native-src]").forEach((image) => {
        const { nativeSrc, nativeSrcset, nativeSizes } = image.dataset;
        if (!nativeSrc) return;

        if (nativeSizes) image.sizes = nativeSizes;
        if (nativeSrcset) image.srcset = nativeSrcset;
        image.src = nativeSrc;
        image.loading = "eager";
        delete image.dataset.nativeSrc;
        delete image.dataset.nativeSrcset;
        delete image.dataset.nativeSizes;
      });
    }

    hydrateAdjacentSlides(index) {
      const total = this.visibleSlides.length;
      if (total < 2) return;

      this.hydrateSlide(this.visibleSlides[circularIndex(index + 1, total)]);
      this.hydrateSlide(this.visibleSlides[circularIndex(index - 1, total)]);
    }

    attachEvents() {
      const { signal } = this.abortController;

      this.mainMedia.addEventListener(
        "scroll",
        () => {
          if (this.scrollEndTimer) window.clearTimeout(this.scrollEndTimer);
          this.scrollEndTimer = window.setTimeout(() => {
            this.pendingIndex = null;
            this.scrollEndTimer = null;
          }, 180);

          if (this.frameId) return;
          this.frameId = window.requestAnimationFrame(() => {
            this.frameId = null;
            this.updateActiveSlide();
          });
        },
        { passive: true, signal },
      );

      this.mainMedia.addEventListener(
        "pointerdown",
        (event) => {
          this.pendingIndex = null;
          if (event.pointerType === "mouse") return;

          this.hydrateAdjacentSlides(this.navigationIndex());

          this.pointerStart = {
            id: event.pointerId,
            x: event.clientX,
            y: event.clientY,
          };
        },
        { passive: true, signal },
      );

      this.mainMedia.addEventListener(
        "pointerup",
        (event) => {
          if (!this.pointerStart || this.pointerStart.id !== event.pointerId)
            return;

          const horizontalDistance = event.clientX - this.pointerStart.x;
          const verticalDistance = event.clientY - this.pointerStart.y;
          this.pointerStart = null;

          if (
            Math.abs(horizontalDistance) < SWIPE_THRESHOLD ||
            Math.abs(horizontalDistance) < Math.abs(verticalDistance)
          ) {
            return;
          }

          const index = this.navigationIndex();
          const lastIndex = this.visibleSlides.length - 1;
          if (horizontalDistance < 0 && index === lastIndex) this.goTo(0);
          if (horizontalDistance > 0 && index === 0) this.goTo(lastIndex);
        },
        { passive: true, signal },
      );

      this.mainMedia.addEventListener(
        "pointercancel",
        () => {
          this.pointerStart = null;
        },
        { passive: true, signal },
      );

      this.mainMedia.addEventListener(
        "keydown",
        (event) => {
          if (event.key === "ArrowRight") {
            event.preventDefault();
            this.goTo(this.activeIndex + 1);
          }
          if (event.key === "ArrowLeft") {
            event.preventDefault();
            this.goTo(this.activeIndex - 1);
          }
          if (event.key === "Home") {
            event.preventDefault();
            this.goTo(0);
          }
          if (event.key === "End") {
            event.preventDefault();
            this.goTo(this.visibleSlides.length - 1);
          }
        },
        { signal },
      );

      this.previousControl?.addEventListener(
        "click",
        (event) => {
          event.preventDefault();
          this.goTo(this.navigationIndex() - 1);
        },
        { signal },
      );

      this.nextControl?.addEventListener(
        "click",
        (event) => {
          event.preventDefault();
          this.goTo(this.navigationIndex() + 1);
        },
        { signal },
      );

      this.mainMedia.addEventListener(
        "click",
        (event) => {
          const trigger = getLightboxTrigger(event, this.mainMedia);
          if (!trigger) return;
          if (
            event.button !== 0 ||
            event.metaKey ||
            event.ctrlKey ||
            event.shiftKey ||
            event.altKey
          )
            return;

          event.preventDefault();
          event.stopPropagation();
          lightbox.open(this.mainMedia, trigger);
        },
        { capture: true, signal },
      );

      this.resizeObserver = new ResizeObserver(() => this.updateActiveSlide());
      this.resizeObserver.observe(this.mainMedia);

      const eventBus = window.eventBus;
      if (eventBus?.on) {
        this.variantHandler = (event) => {
          if (
            event?.sectionId &&
            this.sectionId &&
            event.sectionId !== this.sectionId
          )
            return;
          if (!event?.variant) return;

          this.selectedVariantId = String(event.variant.id);
          this.applyVariantFilter(event.variant.featured_media?.id);
        };
        eventBus.on("variant:change", this.variantHandler);
      }
    }

    applyVariantFilter(featuredMediaId = null) {
      this.visibleSlides = this.slides.filter((slide) =>
        isRelevantMedia(slide, this.selectedVariantId),
      );

      if (this.visibleSlides.length === 0)
        this.visibleSlides = [...this.slides];

      this.slides.forEach((slide) => {
        const visible = this.visibleSlides.includes(slide);
        slide.hidden = !visible;
        if (visible) {
          slide.removeAttribute("aria-hidden");
        } else {
          slide.setAttribute("aria-hidden", "true");
        }
      });

      const featuredSlideIndex = featuredMediaId
        ? this.visibleSlides.findIndex(
            (slide) =>
              String(slide.dataset.mediaId) === String(featuredMediaId),
          )
        : -1;

      const nextIndex = featuredSlideIndex >= 0 ? featuredSlideIndex : 0;
      this.goTo(nextIndex, { instant: true });
    }

    goTo(index, { instant = false } = {}) {
      if (!this.visibleSlides.length) return;

      const nextIndex = circularIndex(index, this.visibleSlides.length);
      const slide = this.visibleSlides[nextIndex];
      const wrapped = nextIndex !== index;
      const isInstant = instant || wrapped || isReducedMotion();

      this.hydrateSlide(slide);
      this.mainMedia.scrollTo({
        left: slide.offsetLeft,
        behavior: isInstant ? "auto" : "smooth",
      });

      if (isInstant) {
        this.pendingIndex = null;
        this.setActiveIndex(nextIndex, { force: true });
      } else {
        this.pendingIndex = nextIndex;
      }
    }

    navigationIndex() {
      return Number.isInteger(this.pendingIndex)
        ? this.pendingIndex
        : this.activeIndex;
    }

    updateActiveSlide() {
      if (!this.visibleSlides.length) return;

      const scrollerCenter =
        this.mainMedia.scrollLeft + this.mainMedia.clientWidth / 2;
      const index = this.visibleSlides.reduce(
        (closestIndex, slide, currentIndex) => {
          const closestSlide = this.visibleSlides[closestIndex];
          const distance = Math.abs(
            slide.offsetLeft + slide.offsetWidth / 2 - scrollerCenter,
          );
          const closestDistance = Math.abs(
            closestSlide.offsetLeft +
              closestSlide.offsetWidth / 2 -
              scrollerCenter,
          );
          return distance < closestDistance ? currentIndex : closestIndex;
        },
        0,
      );

      this.setActiveIndex(index);
    }

    setActiveIndex(index, { force = false } = {}) {
      const hasChanged = this.activeIndex !== index;
      if (!hasChanged && !force) return;

      this.activeIndex = index;
      if (this.pendingIndex === index) this.pendingIndex = null;
      this.hydrateSlide(this.visibleSlides[index]);

      this.visibleSlides.forEach((slide, slideIndex) => {
        const active = slideIndex === index;
        slide.classList.toggle("is-native-active", active);
        slide.setAttribute("aria-current", active ? "true" : "false");
      });

      const total = this.visibleSlides.length;
      if (this.pagination) {
        this.pagination.textContent = `${index + 1} / ${total}`;
        this.pagination.setAttribute(
          "aria-label",
          `Media ${index + 1} of ${total}`,
        );
      }

      const canNavigate = total > 1;
      this.setControlState(this.previousControl, canNavigate);
      this.setControlState(this.nextControl, canNavigate);
    }

    setControlState(control, enabled) {
      if (!control) return;
      control.setAttribute("aria-disabled", String(!enabled));
      control.tabIndex = enabled ? 0 : -1;
      control.classList.toggle("is-disabled", !enabled);
    }
  }

  class NativeProductMediaGrid extends HTMLElement {
    connectedCallback() {
      if (this.initialized || this.initializationQueued) return;

      const mediaRoot = this.querySelector("[data-media-main]");
      if (!mediaRoot) {
        this.queueInitialization();
        return;
      }

      this.initialized = true;
      this.abortController = new AbortController();
      this.mediaRoot = mediaRoot;
      this.mediaWrapper = getMediaWrapper(this);
      this.mobileMedia = this.mediaWrapper?.querySelector("product-media");
      this.sectionId = this.mobileMedia?.dataset.sectionId;
      this.selectedVariantId =
        getSelectedVariantId(this) || getSelectedVariantId(this.mobileMedia);
      this.variantMediaMap = getVariantMediaMap(this);

      this.dataset.nativeProductMediaGrid = "true";
      this.collectAnchors();
      this.applyVariantFilter();
      this.attachEvents();
    }

    queueInitialization() {
      this.initializationQueued = true;
      this.initializationFrame = window.requestAnimationFrame(() => {
        this.initializationQueued = false;
        this.initializationFrame = null;
        if (this.isConnected) this.connectedCallback();
      });
    }

    disconnectedCallback() {
      if (this.initializationFrame)
        window.cancelAnimationFrame(this.initializationFrame);
      this.initializationFrame = null;
      this.initializationQueued = false;
      lightbox.closeForRoot(this.mediaRoot);
      this.abortController?.abort();
      if (this.variantHandler)
        window.eventBus?.off?.("variant:change", this.variantHandler);
      this.initialized = false;
    }

    collectAnchors() {
      this.anchors = [
        ...this.mediaRoot.querySelectorAll("a[data-main-media-link]"),
      ];
      this.mediaItems = [
        ...this.anchors,
        ...this.mediaRoot.querySelectorAll("[data-native-grid-media]"),
      ];

      const mobileMediaByUrl = new Map(
        [...(this.mobileMedia?.querySelectorAll("[data-media-id]") || [])]
          .map((mediaItem) => {
            const mediaLink = mediaItem.querySelector(
              "a[data-main-media-link]",
            );
            return [normaliseMediaUrl(mediaLink?.href), mediaItem];
          })
          .filter(([url]) => Boolean(url)),
      );

      this.anchors.forEach((anchor, index) => {
        const matchingMobileMedia = mobileMediaByUrl.get(
          normaliseMediaUrl(anchor.href),
        );

        if (matchingMobileMedia) {
          anchor.dataset.mediaId ||= matchingMobileMedia.dataset.mediaId;
          anchor.dataset.mediaType ||= matchingMobileMedia.dataset.mediaType;
        }

        addVariantIdsFromMap(anchor, this.variantMediaMap);

        if (!anchor.getAttribute("aria-label")) {
          anchor.setAttribute("aria-label", `View product media ${index + 1}`);
        }
      });
    }

    attachEvents() {
      const { signal } = this.abortController;

      this.mediaRoot.addEventListener(
        "click",
        (event) => {
          const trigger = getLightboxTrigger(event, this.mediaRoot);
          if (!trigger) return;
          if (
            event.button !== 0 ||
            event.metaKey ||
            event.ctrlKey ||
            event.shiftKey ||
            event.altKey
          )
            return;

          event.preventDefault();
          event.stopPropagation();
          lightbox.open(this.mediaRoot, trigger);
        },
        { capture: true, signal },
      );

      const eventBus = window.eventBus;
      if (eventBus?.on) {
        this.variantHandler = (event) => {
          if (
            event?.sectionId &&
            this.sectionId &&
            event.sectionId !== this.sectionId
          )
            return;
          if (!event?.variant) return;

          this.selectedVariantId = String(event.variant.id);
          this.applyVariantFilter();
        };
        eventBus.on("variant:change", this.variantHandler);
      }
    }

    applyVariantFilter() {
      this.mediaItems.forEach((mediaItem) => {
        const visible = isRelevantMedia(mediaItem, this.selectedVariantId);
        mediaItem.hidden = !visible;
        if (visible) {
          mediaItem.removeAttribute("aria-hidden");
        } else {
          mediaItem.setAttribute("aria-hidden", "true");
        }
      });
    }
  }

  if (!window.customElements.get("product-media")) {
    window.customElements.define("product-media", NativeProductMedia);
  } else {
    console.warn(
      "[product-media-native] A product-media controller was registered before the native gallery.",
    );
  }

  if (!window.customElements.get("product-media-grid")) {
    window.customElements.define("product-media-grid", NativeProductMediaGrid);
  } else {
    console.warn(
      "[product-media-native] A product-media-grid controller was registered before the native gallery.",
    );
  }
})();
